import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Alert,
    Chip,
    Stack
} from '@mui/material'

import {
    Add as AddIcon,
    Remove as RemoveIcon,
    SwapHoriz as SwapIcon,
    Send as SendIcon,
    CallReceived as CallReceivedIcon
} from '@mui/icons-material'

import { motion } from 'framer-motion'
import AnimatedSection from '../components/common/AnimatedSection'
import PromotionCarousel from '../components/promotions/PromotionCarousel'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useWallet } from '../contexts/WalletContext'
import api from '../services/api'

const BRAND_ORANGE = '#FF8A00'
const BRAND_ORANGE_DARK = '#E67600'
const POSITIVE_GREEN = '#22c55e'
const MANUAL_BANK_TRANSFER_EMAIL = 'transfer@mojopayr.com'
const diasporaManualRails = {
    'United States': 'Zelle Transfer',
    US: 'Zelle Transfer',
    USA: 'Zelle Transfer',
    Canada: 'Interac e-Transfer Business',
    CA: 'Interac e-Transfer Business',
    'United Kingdom': 'Faster Payments',
    UK: 'Faster Payments',
    GB: 'Faster Payments',
    France: 'SEPA Instant/Open Banking',
    FR: 'SEPA Instant/Open Banking',
    EU: 'SEPA Instant/Open Banking',
    Australia: 'PayID / Osko',
    AU: 'PayID / Osko'
}
const manualBankMethods = [
    'Zelle Transfer',
    'Interac e-Transfer Business',
    'Faster Payments',
    'SEPA Instant/Open Banking',
    'PayID / Osko',
    'Bank-to-Bank Instant Transfer'
]
const countryCodeForFunding = (value) => {
    const normalized = String(value || '').trim().toLowerCase()
    const codes = {
        'united states': 'US',
        'united states of america': 'US',
        usa: 'US',
        us: 'US',
        canada: 'CA',
        ca: 'CA',
        'united kingdom': 'GB',
        'great britain': 'GB',
        uk: 'GB',
        gb: 'GB',
        australia: 'AU',
        au: 'AU',
        france: 'FR',
        fr: 'FR',
        'sierra leone': 'SL',
        sl: 'SL',
        liberia: 'LR',
        lr: 'LR',
        guinea: 'GN',
        gn: 'GN',
        gambia: 'GM',
        'the gambia': 'GM',
        gm: 'GM'
    }
    const resolved = codes[normalized] || String(value || '').trim().toUpperCase()
    return resolved.length <= 10 ? resolved : resolved.slice(0, 10)
}

const Wallet = () => {

    const { user } = useAuth()
    const { lang, t } = useLanguage()
    const wallet = useWallet()
    const isFr = lang === 'fr'

    const [selectedCurrency, setSelectedCurrency] = useState('USD')
    const [fundingOpen, setFundingOpen] = useState(false)
    const [fundingMethod, setFundingMethod] = useState('')
    const [fundingProvider, setFundingProvider] = useState('')
    const [fundingAmount, setFundingAmount] = useState('')
    const [fundingReference, setFundingReference] = useState('')
    const [fundingMessage, setFundingMessage] = useState('')
    const [fundingRequestReference, setFundingRequestReference] = useState('')
    const [fundingRequestMemoCode, setFundingRequestMemoCode] = useState('')
    const [manualFundingStarted, setManualFundingStarted] = useState(false)
    const [fundingError, setFundingError] = useState('')
    const [fundingBusy, setFundingBusy] = useState(false)
    const [fundingInitialized, setFundingInitialized] = useState(false)
    const [stripePublishableKey, setStripePublishableKey] = useState('')
    const [achBankInstructions, setAchBankInstructions] = useState(null)
    const [stripeReady, setStripeReady] = useState(false)
    const stripeRef = useRef(null)
    const stripeElementsRef = useRef(null)
    const cardElementRef = useRef(null)

    const country = user?.country || user?.business_country || 'United States'
    const localCountries = ['Sierra Leone', 'Liberia', 'Guinea']
    const isLocal = localCountries.map((name) => name.toLowerCase()).includes(String(country).toLowerCase())
    const manualBankRail = diasporaManualRails[country] || 'Bank-to-Bank Instant Transfer'
    const fundingMethods = isLocal
        ? ['Mobile Money', 'Bank Transfer']
        : ['Free Bank Transfer', 'Instant Wallet Funding', manualBankRail]
    const mobileMoneyProviders = {
        'Sierra Leone': ['Orange Money', 'Afrimoney'],
        Liberia: ['Orange Money', 'Lonestar MTN Mobile Money'],
        Guinea: ['Orange Money', 'MTN Mobile Money']
    }
    const amountNumber = Number(fundingAmount || 0)
    const isInstantFunding = fundingMethod === 'Instant Wallet Funding'
    const isManualBankTransfer = manualBankMethods.includes(fundingMethod)
    const isAchBankTransfer = fundingMethod === 'Free Bank Transfer'
    const fundingRequestStarted = fundingInitialized || Boolean(fundingMessage && !fundingError)
    const showManualFundingInstructions = manualFundingStarted || (fundingRequestStarted && isManualBankTransfer)
    const showAchFundingInstructions = fundingRequestStarted && isAchBankTransfer
    const fundingMemoCode = fundingRequestMemoCode || (fundingRequestReference ? fundingRequestReference.slice(-6).toUpperCase() : '')
    const instantFee = useMemo(() => {
        if (!isInstantFunding || amountNumber <= 0) return 0
        if (amountNumber <= 100) return Math.max(1.49, amountNumber * 0.015)
        if (amountNumber <= 300) return amountNumber * 0.018
        if (amountNumber <= 500) return amountNumber * 0.0225
        return amountNumber * 0.026
    }, [amountNumber, isInstantFunding])
    const totalCharged = amountNumber + instantFee

    const walletViewData = [
        { currency: 'USD', label: 'US Dollar', flag: 'US', balance: wallet.getBalance('USD'), pending: wallet.getPendingBalance('USD') },
        { currency: 'SLE', label: 'Sierra Leone Leone', flag: 'SL', balance: wallet.getBalance('SLE'), pending: wallet.getPendingBalance('SLE') },
        { currency: 'LRD', label: 'Liberian Dollar', flag: 'LR', balance: wallet.getBalance('LRD'), pending: wallet.getPendingBalance('LRD') },
        { currency: 'GNF', label: 'Guinean Franc', flag: 'GN', balance: wallet.getBalance('GNF'), pending: wallet.getPendingBalance('GNF') }
    ]

    const txRows = Array.isArray(wallet.recentTransactions)
        ? wallet.recentTransactions.map((tx) => {
            const rawAmount = Number(tx.amount ?? 0)
            const isCredit =
                rawAmount > 0 ||
                String(tx.type || '').toLowerCase().includes('plus') ||
                String(tx.direction || '').toLowerCase() === 'credit'
            const currency = String(tx.currency || selectedCurrency || 'USD').toUpperCase()

            return {
                ...tx,
                amountValue: rawAmount,
                isCredit,
                description: tx.description || tx.note || tx.type || (isFr ? 'Transaction' : 'Transaction'),
                date: tx.date || tx.created_at || tx.createdAt || '',
                displayAmount: `${isCredit ? '+' : '-'} ${currency} ${Math.abs(rawAmount).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                })}`,
            }
        })
        : []

    useEffect(() => {
        if (!fundingOpen || fundingMethod !== 'Instant Wallet Funding' || !stripePublishableKey) {
            return undefined
        }

        const mountCard = () => {
            if (!window.Stripe || cardElementRef.current) return

            stripeRef.current = window.Stripe(stripePublishableKey)
            stripeElementsRef.current = stripeRef.current.elements()
            cardElementRef.current = stripeElementsRef.current.create('card', {
                style: {
                    base: {
                        color: '#fff',
                        fontSize: '16px',
                        '::placeholder': { color: 'rgba(255,255,255,0.45)' }
                    },
                    invalid: { color: '#ff7a7a' }
                }
            })
            cardElementRef.current.mount('#stripe-card-element')
            setStripeReady(true)
        }

        if (window.Stripe) {
            mountCard()
        } else {
            let script = document.getElementById('stripe-js')
            if (!script) {
                script = document.createElement('script')
                script.id = 'stripe-js'
                script.src = 'https://js.stripe.com/v3/'
                script.async = true
                document.body.appendChild(script)
            }
            script.addEventListener('load', mountCard)
            return () => script.removeEventListener('load', mountCard)
        }

        return () => {}
    }, [fundingOpen, fundingMethod, stripePublishableKey])

    useEffect(() => {
        if (fundingOpen && (!stripePublishableKey || !achBankInstructions)) {
            api.get('/wallet/funding/settings')
                .then((response) => {
                    setStripePublishableKey(response.data?.publishable_key || '')
                    setAchBankInstructions(response.data?.ach_bank_instructions || null)
                })
                .catch(() => setFundingError(isFr ? 'Les options de recharge ne sont pas disponibles.' : 'Funding options are not available right now.'))
        }

        if (!fundingOpen || fundingMethod !== 'Instant Wallet Funding') {
            if (cardElementRef.current) {
                cardElementRef.current.destroy()
                cardElementRef.current = null
            }
            stripeElementsRef.current = null
            stripeRef.current = null
            setStripeReady(false)
        }
    }, [fundingOpen, fundingMethod, stripePublishableKey, achBankInstructions, isFr])

    const openFunding = () => {
        setFundingError('')
        setFundingMessage('')
        setFundingRequestReference('')
        setFundingRequestMemoCode('')
        setManualFundingStarted(false)
        setFundingInitialized(false)
        setFundingMethod(fundingMethods[0])
        setFundingProvider(isLocal ? (mobileMoneyProviders[country]?.[0] || '') : '')
        setFundingOpen(true)
    }

    const backendFundingMethod = () => {
        if (fundingMethod === 'Instant Wallet Funding') return 'instant_debit'
        if (isManualBankTransfer) return 'manual_bank_to_bank'
        if (fundingMethod === 'Free Bank Transfer') return 'bank_ach'
        if (fundingMethod === 'Mobile Money') return 'mobile_money'
        return 'local_bank'
    }

    const startFunding = async () => {
        setFundingBusy(true)
        setFundingError('')
        setFundingMessage('')
        setFundingRequestReference('')
        setFundingRequestMemoCode('')
        setManualFundingStarted(false)
        setFundingInitialized(false)
        const manualFundingRequest = isManualBankTransfer
        try {
            const fundingPayload = {
                amount: amountNumber,
                currency: selectedCurrency,
                funding_method: backendFundingMethod(),
                provider: fundingMethod === 'Mobile Money'
                    ? fundingProvider
                    : isManualBankTransfer
                        ? fundingMethod
                        : null,
                reference: fundingReference || null,
                country: countryCodeForFunding(country)
            }

            let response
            try {
                response = await api.post('/wallet/deposit', fundingPayload)
            } catch (error) {
                const invalidMethod = error.response?.status === 422 &&
                    String(error.response?.data?.message || '').toLowerCase().includes('funding method')

                if (!isManualBankTransfer || !invalidMethod) {
                    throw error
                }

                response = await api.post('/wallet/deposit', {
                    ...fundingPayload,
                    funding_method: 'bank_ach',
                    provider: fundingMethod,
                    reference: fundingReference || null
                })
            }

            if (fundingMethod === 'Instant Wallet Funding') {
                const clientSecret = response.data?.payment?.client_secret
                const stripePaymentIntentId = response.data?.payment?.stripe_payment_intent_id
                const transactionId = response.data?.transaction_id

                if (!stripeRef.current || !cardElementRef.current || !clientSecret) {
                    throw new Error(isFr ? 'Le formulaire de carte n est pas pret. Veuillez reessayer.' : 'The card form is not ready. Please try again.')
                }

                const result = await stripeRef.current.confirmCardPayment(clientSecret, {
                    payment_method: { card: cardElementRef.current }
                })

                if (result.error) {
                    throw new Error(result.error.message || (isFr ? 'Le paiement par carte de debit n a pas ete finalise.' : 'The debit card payment was not completed.'))
                }

                await api.post(`/wallet/funding/${transactionId}/stripe/confirm`, {
                    stripe_payment_intent_id: stripePaymentIntentId || result.paymentIntent?.id
                })

                setFundingRequestReference(response.data?.reference || '')
                setFundingMessage(isFr ? 'Votre portefeuille a ete recharge et les fonds sont disponibles maintenant.' : 'Your wallet has been funded and the money is available now.')
                setFundingInitialized(true)
            } else {
                const generatedReference = response.data?.reference || response.data?.proof_reference || fundingReference || ''
                const generatedMemoCode = String(response.data?.memo_code || (generatedReference ? generatedReference.slice(-6) : '')).toUpperCase()
                setManualFundingStarted(manualFundingRequest)
                setFundingRequestReference(generatedReference)
                setFundingRequestMemoCode(generatedMemoCode)
                setFundingMessage(
                    manualFundingRequest
                        ? (isFr
                            ? `Votre demande de recharge par ${fundingMethodLabel(fundingMethod)} est en attente de verification MojoPayr. Envoyez le transfert a ${MANUAL_BANK_TRANSFER_EMAIL} et mettez ${generatedMemoCode || 'les 6 derniers caracteres de votre reference MojoPayr'} dans le memo/la reference du transfert. Une fois le transfert confirme, votre portefeuille sera mis a jour.`
                            : `Your ${fundingMethod} funding request is pending MojoPayr review. Send the transfer to ${MANUAL_BANK_TRANSFER_EMAIL} and put ${generatedMemoCode || 'the last 6 characters of your MojoPayr reference'} in the transfer memo/reference field. Once MojoPayr confirms it, your wallet will be updated.`)
                        : response.data?.message || (isFr ? 'Votre demande de recharge a commence.' : 'Your funding request has started.')
                )
                setFundingInitialized(true)
            }

            await wallet.refreshWallet()
        } catch (error) {
            setFundingError(
                error.response?.data?.message ||
                error.message ||
                (isFr ? 'La recharge n a pas pu demarrer. Verifiez les details et reessayez.' : 'Funding could not be started. Please check the details and try again.')
            )
        } finally {
            setFundingBusy(false)
        }
    }

    const fundingMethodLabel = (method) => {
        if (!isFr) return method
        return {
            'Mobile Money': 'Mobile Money',
            'Bank Transfer': 'Virement bancaire',
            'Free Bank Transfer': 'Virement bancaire gratuit',
            'Instant Wallet Funding': 'Recharge instantanee',
            'Zelle Transfer': 'Zelle',
            'Interac e-Transfer': 'Interac e-Transfer',
            'Interac e-Transfer Business': 'Interac e-Transfer Business',
            'Faster Payments': 'Faster Payments',
            'SEPA Instant': 'SEPA Instant',
            'SEPA Instant/Open Banking': 'SEPA Instant/Open Banking',
            'PayID / Osko': 'PayID / Osko',
            'Bank-to-Bank Instant Transfer': 'Virement instantane',
        }[method] || method
    }

    return (

        <Box sx={{ minHeight: '100vh', bgcolor: '#0D0221' }}>

            <Container maxWidth="lg" sx={{ py: 4 }}>

                <AnimatedSection>

                    <Typography
                        variant="h4"
                        sx={{ color: '#fff', fontWeight: 800, mb: 1 }}
                    >
                        {t('wallet.title', null, 'My Wallet')}
                    </Typography>

                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', mb: 4 }}>
                        {t('wallet.subtitle', null, 'Manage your balances and track your transactions')}
                    </Typography>

                    <PromotionCarousel
                        platform="website"
                        country={String(user?.country || '').toUpperCase()}
                        currency={selectedCurrency}
                        userType={String(user?.accountType || user?.type || '').toUpperCase()}
                        fallbackTitle={isFr ? 'Mises a jour du portefeuille' : 'Wallet updates'}
                        fallbackMessage={
                            isFr
                                ? 'Ajoutez des fonds ou envoyez directement vers un autre portefeuille MojoPayr.'
                                : 'Top up fast or move value directly into another MojoPayr wallet.'
                        }
                        compact
                        sx={{ mb: 3 }}
                    />

                    <Grid container spacing={3} sx={{ mb: 4 }}>

                        {walletViewData.map((wallet, index) => (

                            <Grid item xs={12} sm={6} md={3} key={wallet.currency}>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >

                                    <Card
                                        onClick={() => setSelectedCurrency(wallet.currency)}
                                        sx={{
                                            cursor: 'pointer',
                                            background: 'rgba(27,16,58,0.95)',
                                            border: selectedCurrency === wallet.currency
                                                ? `2px solid ${BRAND_ORANGE}`
                                                : '1px solid rgba(255,255,255,0.08)',
                                            backdropFilter: 'blur(12px)'
                                        }}
                                    >

                                        <CardContent>

                                            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                                                {wallet.flag} {wallet.currency}
                                            </Typography>

                                            <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 2 }}>
                                                {wallet.label}
                                            </Typography>

                                            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800 }}>
                                                {wallet.balance.toFixed(2)}
                                            </Typography>

                                            <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                                {t('wallet.availableBalance', null, 'Available Balance')}
                                            </Typography>

                                            <Typography sx={{ color: 'rgba(255,255,255,0.55)', mt: 1 }}>
                                                {t('common.pending', null, 'Pending')} {wallet.pending.toFixed(2)}
                                            </Typography>

                                        </CardContent>

                                    </Card>

                                </motion.div>

                            </Grid>

                        ))}

                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 4 }}>

                        <Grid item xs={12} md={4}>
                            <Button
                                fullWidth
                                size="large"
                                startIcon={<AddIcon />}
                                onClick={openFunding}
                                sx={{
                                    bgcolor: BRAND_ORANGE,
                                    color: '#fff',
                                    fontWeight: 700,
                                    '&:hover': { bgcolor: BRAND_ORANGE_DARK }
                                }}
                            >
                                {t('wallet.addMoney', null, 'Add Money')}
                            </Button>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Button
                                fullWidth
                                size="large"
                                startIcon={<RemoveIcon />}
                                sx={{
                                    bgcolor: BRAND_ORANGE,
                                    color: '#fff',
                                    fontWeight: 700,
                                    '&:hover': { bgcolor: BRAND_ORANGE_DARK }
                                }}
                            >
                                {t('wallet.withdraw', null, 'Withdraw')}
                            </Button>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                size="large"
                                startIcon={<SwapIcon />}
                                sx={{
                                    borderColor: BRAND_ORANGE,
                                    color: BRAND_ORANGE,
                                    fontWeight: 700,
                                    '&:hover': {
                                        background: 'rgba(255,138,0,0.1)',
                                        borderColor: BRAND_ORANGE
                                    }
                                }}
                            >
                                {t('wallet.convert', null, 'Convert Currency')}
                            </Button>
                        </Grid>

                    </Grid>

                    <Card
                        sx={{
                            background: 'rgba(27,16,58,0.95)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            backdropFilter: 'blur(12px)'
                        }}
                    >

                        <CardContent>

                            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
                                {t('wallet.recentTransactions', null, 'Recent Transactions')}
                            </Typography>

                            {txRows.length === 0 ? (

                                <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {isFr ? 'Aucune transaction pour le moment.' : 'No transactions yet.'}
                                </Typography>

                            ) : (

                                <List>

                                    {txRows.map((tx, i) => {

                                        const Icon = tx.isCredit ? CallReceivedIcon : SendIcon

                                        return (

                                            <React.Fragment key={i}>

                                                <ListItem>

                                                    <ListItemAvatar>

                                                        <Avatar
                                                            sx={{
                                                                bgcolor: tx.isCredit
                                                                    ? 'rgba(34,197,94,0.15)'
                                                                    : 'rgba(255,138,0,0.15)'
                                                            }}
                                                        >

                                                            <Icon
                                                                sx={{
                                                                    color: tx.isCredit
                                                                        ? POSITIVE_GREEN
                                                                        : BRAND_ORANGE
                                                                }}
                                                            />

                                                        </Avatar>

                                                    </ListItemAvatar>

                                                    <ListItemText
                                                        primary={tx.description}
                                                        secondary={tx.date}
                                                        primaryTypographyProps={{ sx: { color: '#fff', fontWeight: 600 } }}
                                                        secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.6)' } }}
                                                    />

                                                    <Typography
                                                        sx={{
                                                            color: tx.isCredit ? POSITIVE_GREEN : '#fff',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {tx.displayAmount}
                                                    </Typography>

                                                </ListItem>

                                                {i < txRows.length - 1 && <Divider />}

                                            </React.Fragment>

                                        )

                                    })}

                                </List>

                            )}

                        </CardContent>

                    </Card>

                </AnimatedSection>

                <Dialog
                    open={fundingOpen}
                    onClose={() => setFundingOpen(false)}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{
                        sx: {
                            bgcolor: '#1B103A',
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.08)'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 800 }}>
                        {t('wallet.dialog.deposit.title', null, 'Funding your wallet')}
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                            {isFr
                                ? 'Choisissez comment approvisionner votre portefeuille MojoPayr.'
                                : 'Choose how you want to fund your MojoPayr wallet.'}
                        </Typography>

                        {fundingRequestStarted ? (
                            <>
                                {fundingMessage && <Alert severity="success" sx={{ mb: 2 }}>{fundingMessage}</Alert>}
                                {(showManualFundingInstructions || showAchFundingInstructions) && (
                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, p: 2 }}>
                                        <FundingRow label={isFr ? 'Reference MojoPayr' : 'MojoPayr Reference'} value={fundingRequestReference || '-'} highlight />
                                        <FundingRow label={isFr ? 'Code memo' : 'Memo Code'} value={fundingMemoCode || '-'} highlight />
                                        {showManualFundingInstructions && <FundingRow label={isFr ? 'Envoyer a' : 'Send to'} value={MANUAL_BANK_TRANSFER_EMAIL} />}
                                        {showAchFundingInstructions && achBankInstructions && (
                                            <>
                                                <FundingRow label={isFr ? 'Banque' : 'Bank Name'} value={achBankInstructions.bank_name} />
                                                <FundingRow label={isFr ? 'Titulaire du compte' : 'Account Holder'} value={achBankInstructions.account_holder} />
                                                <FundingRow label={isFr ? 'Numero de compte' : 'Account Number'} value={achBankInstructions.account_number} copyable />
                                                <FundingRow label={isFr ? 'Numero de routage ACH' : 'ACH Routing Number'} value={achBankInstructions.routing_number} copyable />
                                                <FundingRow label={isFr ? 'Type de compte' : 'Account Type'} value={achBankInstructions.account_type} />
                                            </>
                                        )}
                                        <FundingRow label={isFr ? 'Montant' : 'Amount'} value={`${selectedCurrency} ${amountNumber.toFixed(2)}`} />
                                        <Typography sx={{ color: 'rgba(255,255,255,0.65)', mt: 1 }}>
                                            {isFr
                                                ? 'Mettez seulement le code memo de 6 caracteres dans la note du transfert.'
                                                : `Put only the 6-character memo code${fundingMemoCode ? ` (${fundingMemoCode})` : ''} in the transfer note or memo.`}
                                        </Typography>
                                        {fundingMemoCode && (
                                            <Button
                                                onClick={() => navigator.clipboard?.writeText(fundingMemoCode)}
                                                sx={{ color: BRAND_ORANGE, mt: 1, px: 0, fontWeight: 800 }}
                                            >
                                                {isFr ? 'Copier le code memo' : 'Copy Memo Code'}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </>
                        ) : (
                            <>
                        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            {fundingMethod === 'Free Bank Transfer' && <Chip label={isFr ? 'Gratuit' : 'Free'} sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: POSITIVE_GREEN }} />}
                            {fundingMethod === 'Instant Wallet Funding' && <Chip label={isFr ? 'Instantane' : 'Instant'} sx={{ bgcolor: 'rgba(255,138,0,0.15)', color: BRAND_ORANGE }} />}
                            {isManualBankTransfer && <Chip label={isFr ? 'Revision manuelle' : 'Manual review'} sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' }} />}
                            {fundingMethod === 'Mobile Money' && <Chip label={isFr ? 'Recommande' : 'Recommended'} sx={{ bgcolor: 'rgba(255,138,0,0.15)', color: BRAND_ORANGE }} />}
                            {fundingMethod === 'Bank Transfer' && <Chip label={isFr ? 'Gratuit ou frais bas' : 'Free or low fee'} sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' }} />}
                        </Stack>

                        {fundingError && <Alert severity="error" sx={{ mb: 2 }}>{fundingError}</Alert>}
                        {fundingMessage && <Alert severity="success" sx={{ mb: 2 }}>{fundingMessage}</Alert>}

                        <TextField
                            select
                            fullWidth
                            label={isFr ? 'Methode de recharge' : 'Funding method'}
                            value={fundingMethod}
                            onChange={(event) => setFundingMethod(event.target.value)}
                            sx={{ mb: 2 }}
                            InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                            InputProps={{ sx: { color: '#fff' } }}
                        >
                            {fundingMethods.map((method) => (
                                <MenuItem key={method} value={method}>{fundingMethodLabel(method)}</MenuItem>
                            ))}
                        </TextField>

                        {fundingMethod === 'Mobile Money' && (
                            <TextField
                                select
                            fullWidth
                            label={isFr ? 'Fournisseur' : 'Provider'}
                                value={fundingProvider}
                                onChange={(event) => setFundingProvider(event.target.value)}
                                sx={{ mb: 2 }}
                                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                                InputProps={{ sx: { color: '#fff' } }}
                            >
                                {(mobileMoneyProviders[country] || ['Mobile Money']).map((provider) => (
                                    <MenuItem key={provider} value={provider}>{provider}</MenuItem>
                                ))}
                            </TextField>
                        )}

                        <TextField
                            fullWidth
                            type="number"
                            label={`${isFr ? 'Montant a ajouter' : 'Amount to add'} (${selectedCurrency})`}
                            value={fundingAmount}
                            onChange={(event) => setFundingAmount(event.target.value)}
                            sx={{ mb: 2 }}
                            InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                            InputProps={{ sx: { color: '#fff' } }}
                        />

                        <TextField
                            fullWidth
                            label={
                                isManualBankTransfer
                                    ? (isFr ? 'Reference expediteur facultative' : 'Sender reference optional')
                                    : (isFr ? 'Reference facultative' : 'Reference optional')
                            }
                            placeholder={isManualBankTransfer
                                ? (isFr
                                    ? 'MojoPayr generera la reference requise apres le demarrage'
                                    : 'MojoPayr will generate the required reference after you start funding')
                                : undefined}
                            value={fundingReference}
                            onChange={(event) => setFundingReference(event.target.value)}
                            sx={{ mb: 2 }}
                            InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.7)' } }}
                            InputProps={{ sx: { color: '#fff' } }}
                        />

                        {isManualBankTransfer && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {isFr
                                    ? `Envoyez via ${fundingMethodLabel(fundingMethod)} a ${MANUAL_BANK_TRANSFER_EMAIL}. Utilisez le code memo pour que MojoPayr puisse verifier et crediter votre portefeuille.`
                                    : `Send via ${fundingMethodLabel(fundingMethod)} to ${MANUAL_BANK_TRANSFER_EMAIL}. Use the memo code so MojoPayr can verify and credit your wallet.`}
                            </Alert>
                        )}

                        {isAchBankTransfer && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {achBankInstructions
                                    ? (isFr ? 'Apres avoir demarre, utilisez les coordonnees ACH affichees et ajoutez obligatoirement votre code memo.' : 'After starting, use the ACH details shown and include your required memo code.')
                                    : (isFr ? 'Les instructions bancaires ACH ne sont pas encore configurees.' : 'ACH bank instructions are not configured yet.')}
                            </Alert>
                        )}

                        {fundingMethod === 'Instant Wallet Funding' && (
                            <Box
                                id="stripe-card-element"
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    minHeight: 54,
                                    borderRadius: 2,
                                    border: '1px solid rgba(255,255,255,0.18)',
                                    bgcolor: 'rgba(255,255,255,0.04)'
                                }}
                            />
                        )}

                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, p: 2 }}>
                            <FundingRow label={isFr ? 'Montant a ajouter' : 'Amount to add'} value={`${selectedCurrency} ${amountNumber.toFixed(2)}`} />
                            {isManualBankTransfer && <FundingRow label={isFr ? 'Envoyer a' : 'Send to'} value={MANUAL_BANK_TRANSFER_EMAIL} />}
                            <FundingRow label={isFr ? 'Frais de recharge instantanee' : 'Instant funding fee'} value={`${selectedCurrency} ${instantFee.toFixed(2)}`} />
                            <FundingRow label={isFr ? 'Total facture' : 'Total charged'} value={`${selectedCurrency} ${totalCharged.toFixed(2)}`} highlight />
                            <FundingRow label={isFr ? 'Portefeuille recoit' : 'Wallet receives'} value={`${selectedCurrency} ${amountNumber.toFixed(2)}`} />
                            <Typography sx={{ color: 'rgba(255,255,255,0.65)', mt: 1 }}>
                                {fundingMethod === 'Free Bank Transfer' && (isFr ? 'Disponible en 1 a 3 jours ouvrables.' : 'Available in 1-3 business days.')}
                                {fundingMethod === 'Instant Wallet Funding' && (isFr ? 'Disponible immediatement. De petits frais s appliquent.' : 'Available immediately. A small fee applies.')}
                                {isManualBankTransfer && (isFr ? 'En attente de verification MojoPayr.' : 'Pending MojoPayr review after your transfer is received.')}
                                {fundingMethod === 'Mobile Money' && (isFr ? 'Disponible instantanement ou presque. De petits frais s appliquent.' : 'Available instantly or near real-time. Small fee applies.')}
                                {fundingMethod === 'Bank Transfer' && (isFr ? 'Le delai de traitement peut varier.' : 'Processing time may vary.')}
                            </Typography>
                        </Box>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={() => setFundingOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            {t('common.close', null, 'Close')}
                        </Button>
                        {!fundingRequestStarted && (
                            <Button
                                onClick={startFunding}
                                disabled={
                                    fundingBusy ||
                                    amountNumber <= 0 ||
                                    !fundingMethod ||
                                    (isAchBankTransfer && !achBankInstructions) ||
                                    (fundingMethod === 'Instant Wallet Funding' && !stripeReady)
                                }
                                sx={{
                                    bgcolor: BRAND_ORANGE,
                                    color: '#fff',
                                    fontWeight: 800,
                                    '&:hover': { bgcolor: BRAND_ORANGE_DARK }
                                }}
                            >
                                {fundingBusy
                                    ? (isFr ? 'Demarrage...' : 'Starting...')
                                    : (isFr ? 'Demarrer la recharge' : 'Start Funding')}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>

            </Container>

        </Box>

    )

}

const FundingRow = ({ label, value, highlight = false, copyable = false }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, gap: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.65)' }}>{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: highlight ? BRAND_ORANGE : '#fff', fontWeight: 800, textAlign: 'right' }}>
                {value}
            </Typography>
            {copyable && value && (
                <Button size="small" onClick={() => navigator.clipboard?.writeText(String(value))} sx={{ minWidth: 0, color: BRAND_ORANGE, fontWeight: 800 }}>
                    Copy
                </Button>
            )}
        </Box>
    </Box>
)

export default Wallet
